import { exportXLSX } from "./exportXLSX";
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, TextField, Alert, LinearProgress,
  Button, TableSortLabel, Breadcrumbs, Link, Chip
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";

const API = process.env.REACT_APP_API_URL || "";
const num = (v) => v != null ? Number(v).toLocaleString("es-MX") : "0";
const pct = (v) => v != null ? `${Number(v).toFixed(1)}%` : "0%";
const headerSx = { fontWeight: 700, bgcolor: "#AF272F", color: "#fff" };
const sortSx = {"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"};

function exportCSV(rows, columns, labels, filename) {
  const header = labels.join(",");
  const csv = [header, ...rows.map(r => columns.map(c => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const getColor = (val, min, max) => {
  if (val == null || max === min) return "#9e9e9e";
  const ratio = (val - min) / (max - min);
  if (ratio >= 0.75) return "#2e7d32";
  if (ratio >= 0.5) return "#689f38";
  if (ratio >= 0.25) return "#f9a825";
  return "#c62828";
};

function ReportesActividadUsuario({ token }) {
  const [view, setView] = useState("entidad");
  const [data, setData] = useState([]);
  const [selectedEntidad, setSelectedEntidad] = useState(null);
  const [selectedDistrito, setSelectedDistrito] = useState(null);
  const [selectedPlaza, setSelectedPlaza] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("afiliados");
  const [sortDir, setSortDir] = useState("desc");
  const [highlightId, setHighlightId] = useState(null);

  // Perfil territorial del usuario
  const perfil = JSON.parse(localStorage.getItem("perfil_territorial") || "null");
  const roleCns = JSON.parse(localStorage.getItem("roles") || "[]");
  const soloEstructura = (roleCns.includes("reportes_estructura") || (perfil && ((perfil.cargo_id >= 3 && perfil.cargo_id <= 7) || perfil.cargo_id === 13))) && !roleCns.includes("compulsa") && !roleCns.includes("monitoreo") && !roleCns.includes("nacional");
  const tipoTerritorio = (soloEstructura && perfil?.afiliacion_tipo) ? perfil.afiliacion_tipo : "NACIONAL";

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async (url) => {
    setLoading(true); setError("");
    try {
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setData(json.data || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (tipoTerritorio === "DISTRITAL" && perfil?.entidad && perfil?.distrito) {
      setView("plaza");
      setSelectedEntidad({ entidad: perfil.entidad });
      setSelectedDistrito({ distrito: perfil.distrito });
      fetchData(`${API}/api/actividad-usuario/por-plaza?entidad=${perfil.entidad}&distrito=${perfil.distrito}`);
    } else if (tipoTerritorio === "ESTATAL" && perfil?.entidad) {
      setView("distrito");
      setSelectedEntidad({ entidad: perfil.entidad });
      fetchData(`${API}/api/actividad-usuario/por-distrito?entidad=${perfil.entidad}`);
    } else if (tipoTerritorio === "CIRCUNSCRIPCIONAL" && perfil?.circunscripcion) {
      fetchData(`${API}/api/actividad-usuario/por-entidad?circunscripcion=${perfil.circunscripcion}`);
    } else if (tipoTerritorio === "REGIONAL" && perfil?.region_decimal) {
      fetchData(`${API}/api/actividad-usuario/por-entidad?region_decimal=${perfil.region_decimal}`);
    } else {
      fetchData(`${API}/api/actividad-usuario/por-entidad`);
    }
  }, [fetchData]);

  const handleEntidadClick = (row) => { setSelectedEntidad(row); setView("distrito"); setSearch(""); fetchData(`${API}/api/actividad-usuario/por-distrito?entidad=${row.entidad}`); };
  const handleDistritoClick = (row) => { setSelectedDistrito(row); setView("plaza"); setSearch(""); fetchData(`${API}/api/actividad-usuario/por-plaza?entidad=${selectedEntidad.entidad}&distrito=${row.distrito}`); };
  const handlePlazaClick = (row) => { setSelectedPlaza(row); setView("seccion"); setSearch(""); fetchData(`${API}/api/actividad-usuario/por-seccion-plaza?plaza_id=${row.plaza_id}`); };
  const nivelMinimo = tipoTerritorio === "DISTRITAL" ? "plaza" : tipoTerritorio === "ESTATAL" ? "distrito" : "entidad";
  const handleBack = () => {
    if (view === "seccion") { setView("plaza"); setSelectedPlaza(null); setSearch(""); fetchData(`${API}/api/actividad-usuario/por-plaza?entidad=${selectedEntidad.entidad}&distrito=${selectedDistrito.distrito}`); }
    else if (view === "plaza" && nivelMinimo !== "plaza") { setView("distrito"); setSelectedDistrito(null); setSearch(""); fetchData(`${API}/api/actividad-usuario/por-distrito?entidad=${selectedEntidad.entidad}`); }
    else if (view === "distrito" && nivelMinimo === "entidad") { setView("entidad"); setSelectedEntidad(null); setSearch(""); fetchData(`${API}/api/actividad-usuario/por-entidad`); }
  };
  const canGoBack = (view === "seccion") || (view === "plaza" && nivelMinimo !== "plaza") || (view === "distrito" && nivelMinimo === "entidad");
  const handleRefresh = () => {
    if (view === "entidad") fetchData(`${API}/api/actividad-usuario/por-entidad`);
    else if (view === "distrito") fetchData(`${API}/api/actividad-usuario/por-distrito?entidad=${selectedEntidad.entidad}`);
    else if (view === "plaza") fetchData(`${API}/api/actividad-usuario/por-plaza?entidad=${selectedEntidad.entidad}&distrito=${selectedDistrito.distrito}`);
    else fetchData(`${API}/api/actividad-usuario/por-seccion-plaza?plaza_id=${selectedPlaza.plaza_id}`);
  };
  const handleSort = (col) => { if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(col); setSortDir("desc"); } };

  const filtered = data.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.entidad_nombre || "").toLowerCase().includes(s)
      || (r.distrito_federal_nombre || "").toLowerCase().includes(s)
      || (r.usuario_nombre || "").toLowerCase().includes(s)
      || (r.uid || "").toLowerCase().includes(s);
  }).sort((a, b) => {
    const av = a[sortBy], bv = b[sortBy];
    const an = Number(av), bn = Number(bv);
    const cmp = (!isNaN(an) && !isNaN(bn)) ? an - bn : String(av || "").localeCompare(String(bv || ""), "es");
    return sortDir === "desc" ? -cmp : cmp;
  });

  const totalAfil = data.reduce((s, r) => s + Number(r.afiliados || 0), 0);
  const totalVal = data.reduce((s, r) => s + Number(r.validados || 0), 0);
  const totalImpr = data.reduce((s, r) => s + Number(r.imp_impresas || 0), 0);
  const totalEntr = data.reduce((s, r) => s + Number(r.imp_entregadas || 0), 0);
  const totalConv = data.reduce((s, r) => s + Number(r.convencidos_total || r.conv_ruta || 0), 0);

  const vals = filtered.map(r => Number(r.pct_efectividad || 0)).filter(v => v > 0);
  const minVal = vals.length ? Math.min(...vals) : 0;
  const maxVal = vals.length ? Math.max(...vals) : 100;

  const title = view === "entidad" ? "Actividad por Usuario — Nacional"
    : view === "distrito" ? `Actividad por Usuario — ${selectedEntidad?.entidad_nombre || (data.length > 0 ? data[0].entidad_nombre : "")}`
    : view === "plaza" ? `Actividad por Usuario — ${selectedEntidad?.entidad_nombre || ""} Dto. ${selectedDistrito?.distrito}`
    : `${selectedPlaza?.nombre_cot || selectedPlaza?.plaza} — Secciones`;

  const cols = view === "entidad"
    ? [{k:"entidad_nombre",l:"Entidad"},{k:"total_usuarios",l:"COTs",a:"right"},{k:"afiliados",l:"Afiliados",a:"right"},{k:"validados",l:"Validados",a:"right"},{k:"pct_efectividad",l:"% Efectividad",a:"center"},{k:"imp_impresas",l:"Impresas",a:"right"},{k:"imp_entregadas",l:"Entregadas",a:"right"},{k:"convencidos_total",l:"Convencidos",a:"right"},{k:"convencidos_reales",l:"Conv. Reales",a:"right"},{k:"visitas",l:"Visitas",a:"right"}]
    : view === "distrito"
    ? [{k:"distrito",l:"Dto."},{k:"distrito_federal_nombre",l:"Cabecera"},{k:"total_usuarios",l:"COTs",a:"right"},{k:"afiliados",l:"Afiliados",a:"right"},{k:"validados",l:"Validados",a:"right"},{k:"pct_efectividad",l:"% Efectividad",a:"center"},{k:"imp_impresas",l:"Impresas",a:"right"},{k:"imp_entregadas",l:"Entregadas",a:"right"},{k:"convencidos_total",l:"Convencidos",a:"right"},{k:"convencidos_reales",l:"Conv. Reales",a:"right"},{k:"visitas",l:"Visitas",a:"right"}]
    : view === "plaza"
    ? [{k:"plaza",l:"Puesto"},{k:"nombre_cot",l:"COT"},{k:"telefono",l:"Teléfono"},{k:"ocupado",l:"Ocupado",a:"center"},{k:"secciones_ruta",l:"Secciones",a:"right"},{k:"afiliados",l:"Afiliados",a:"right"},{k:"validados",l:"Validados",a:"right"},{k:"pct_efectividad",l:"% Efectividad",a:"center"},{k:"imp_impresas",l:"Impresas",a:"right"},{k:"imp_entregadas",l:"Entregadas",a:"right"},{k:"conv_ruta",l:"Convencidos en Ruta",a:"right"},{k:"conv_cot",l:"Convencidos por COT",a:"right"},{k:"visitas",l:"Visitas",a:"right"}]
    : [{k:"seccion",l:"Sección"},{k:"afiliados",l:"Registros",a:"right"},{k:"validados",l:"Verificados",a:"right"},{k:"imp_impresas",l:"Cred. Impresas",a:"right"},{k:"imp_entregadas",l:"Cred. Entregadas",a:"right"},{k:"conv_ruta",l:"Convencidos en Ruta",a:"right"},{k:"conv_cot",l:"Convencidos por COT",a:"right"}];

  return (
    <Box sx={{ width: "100%", mx: "auto", px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        {canGoBack && <IconButton onClick={handleBack} sx={{ color: "#AF272F" }}><ArrowBackIcon /></IconButton>}
        <Typography variant="h5" sx={{ color: "#AF272F", fontWeight: 700, flexGrow: 1 }}>{title}</Typography>
        <IconButton onClick={handleRefresh} title="Actualizar"><RefreshIcon /></IconButton>
        <Button size="small" startIcon={<DownloadIcon />} onClick={() => exportXLSX(filtered, cols, `actividad_usuario_${view}_${new Date().toISOString().slice(0,10)}`)} sx={{ color: "#AF272F" }}>Excel</Button>
      </Box>

      <Breadcrumbs sx={{ mb: 2 }}>
        {nivelMinimo === "entidad" && <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === "entidad" ? 700 : 400, color: view === "entidad" ? "#AF272F" : "inherit" }} onClick={() => { setView("entidad"); setSelectedEntidad(null); setSearch(""); fetchData(`${API}/api/actividad-usuario/por-entidad`); }}>Nacional</Link>}
        {(view === "distrito" || view === "plaza" || view === "seccion") && <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === "distrito" ? 700 : 400, color: "#AF272F" }} onClick={() => { if (nivelMinimo !== "distrito") { setView("distrito"); setSelectedDistrito(null); setSearch(""); fetchData(`${API}/api/actividad-usuario/por-distrito?entidad=${selectedEntidad.entidad}`); } }}>{selectedEntidad?.entidad_nombre || (data.length > 0 ? data[0].entidad_nombre : "")}</Link>}
        {(view === "plaza" || view === "seccion") && <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === "plaza" ? 700 : 400, color: "#AF272F" }} onClick={() => { if (nivelMinimo !== "plaza") { setView("plaza"); setSelectedPlaza(null); setSearch(""); fetchData(`${API}/api/actividad-usuario/por-plaza?entidad=${selectedEntidad.entidad}&distrito=${selectedDistrito.distrito}`); } }}>Dto. {selectedDistrito?.distrito}</Link>}
        {view === "seccion" && <Typography color="#AF272F" fontWeight={700}>{selectedPlaza?.nombre_cot || selectedPlaza?.plaza}</Typography>}
      </Breadcrumbs>

      {/* KPIs */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 110, textAlign: "center", borderTop: "3px solid #1565c0" }}>
          <Typography variant="h6" fontWeight={700}>{num(totalAfil)}</Typography>
          <Typography variant="caption" color="text.secondary">Afiliados</Typography>
        </Paper>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 110, textAlign: "center", borderTop: "3px solid #6a1b9a" }}>
          <Typography variant="h6" fontWeight={700}>{num(totalVal)}</Typography>
          <Typography variant="caption" color="text.secondary">Validados</Typography>
        </Paper>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 110, textAlign: "center", borderTop: "3px solid #e65100" }}>
          <Typography variant="h6" fontWeight={700}>{num(totalImpr)}</Typography>
          <Typography variant="caption" color="text.secondary">Impresas</Typography>
        </Paper>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 110, textAlign: "center", borderTop: "3px solid #00695c" }}>
          <Typography variant="h6" fontWeight={700}>{num(totalEntr)}</Typography>
          <Typography variant="caption" color="text.secondary">Entregadas</Typography>
        </Paper>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 110, textAlign: "center", borderTop: "3px solid #AF272F" }}>
          <Typography variant="h6" fontWeight={700}>{num(totalConv)}</Typography>
          <Typography variant="caption" color="text.secondary">Convencidos</Typography>
        </Paper>
      </Box>

      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <SearchIcon sx={{ color: "text.secondary" }} />
        <TextField size="small" placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} sx={{ width: { xs: "100%", sm: 250, md: 300 } }} />
        <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>{filtered.length} registros</Typography>
      </Box>

      {/* Mejor / Menor */}
      {filtered.length > 1 && (() => {
        const dataConVal = filtered.filter(r => view === "seccion" ? Number(r.meta) > 0 : Number(r.afiliados || r.convencidos_total || r.meta) > 0);
        if (dataConVal.length < 2) return null;
        const metric = view === "seccion" ? "pct_convencidos" : "pct_efectividad";
        const sorted = [...dataConVal].sort((a, b) => Number(b[metric]) - Number(a[metric]));
        const top = sorted[0]; const bottom = sorted[sorted.length - 1];
        if (top === bottom) return null;
        const getName = (r) => view === "seccion" ? `Sección ${r.seccion}` : view === "plaza" ? (r.nombre_cot || r.plaza) : view === "distrito" ? `Dto. ${r.distrito} - ${r.distrito_federal_nombre}` : r.entidad_nombre;
        const topId = top.seccion || top.plaza_id || top.distrito || top.entidad;
        const bottomId = bottom.seccion || bottom.plaza_id || bottom.distrito || bottom.entidad;
        return (
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Paper onClick={() => { setHighlightId(topId); setTimeout(() => { const el = document.getElementById(`row-${topId}`); if(el) el.scrollIntoView({behavior:"smooth",block:"center"}); }, 100); }} sx={{ p: 1.5, flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 1, borderLeft: "4px solid #2e7d32", cursor: "pointer", "&:hover": { bgcolor: "#e8f5e9" } }}>
              <Box><Typography variant="body2" fontWeight={700} sx={{ color: "#2e7d32" }}>Mejor: {getName(top)}</Typography><Typography variant="caption">{pct(top[metric])} {view === "seccion" ? "avance" : "efectividad"}</Typography></Box>
            </Paper>
            <Paper onClick={() => { setHighlightId(bottomId); setTimeout(() => { const el = document.getElementById(`row-${bottomId}`); if(el) el.scrollIntoView({behavior:"smooth",block:"center"}); }, 100); }} sx={{ p: 1.5, flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 1, borderLeft: "4px solid #c62828", cursor: "pointer", "&:hover": { bgcolor: "#ffebee" } }}>
              <Box><Typography variant="body2" fontWeight={700} sx={{ color: "#c62828" }}>Menor: {getName(bottom)}</Typography><Typography variant="caption">{pct(bottom[metric])} {view === "seccion" ? "avance" : "efectividad"}</Typography></Box>
            </Paper>
          </Box>
        );
      })()}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, "& .MuiLinearProgress-bar": { bgcolor: "#AF272F" } }} />}

      <TableContainer component={Paper} sx={{ maxHeight: "60vh", overflowX: "auto", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {cols.map(c => (
                <TableCell key={c.k} align={c.a || "left"} sx={headerSx}>
                  <TableSortLabel active={sortBy===c.k} direction={sortBy===c.k?sortDir:"asc"} onClick={()=>handleSort(c.k)} sx={sortSx}>{c.l}</TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row, i) => {
              const clickable = view !== "seccion";
              const pctVal = Number(row.pct_efectividad || row.pct_convencidos || 0);
              const color = getColor(pctVal, minVal, maxVal);
              const rowId = row.seccion || row.plaza_id || row.distrito || row.entidad;
              return (
                <TableRow key={i} hover={clickable} id={`row-${rowId}`}
                  onClick={() => { if (view === "entidad") handleEntidadClick(row); else if (view === "distrito") handleDistritoClick(row); else if (view === "plaza") handlePlazaClick(row); }}
                  sx={{...(clickable ? { cursor: "pointer", "&:hover": { bgcolor: "#fce4ec" } } : {}), bgcolor: highlightId === rowId ? "#fff9c4" : "inherit", transition: "background 0.5s"}}>
                  {cols.map(c => (
                    <TableCell key={c.k} align={c.a || "left"}>
                      {(c.k === "pct_efectividad" || c.k === "pct_convencidos") ? (
                        <Chip label={pct(row[c.k])} size="small" sx={{ bgcolor: color, color: "#fff", fontWeight: 700, fontSize: "0.75rem", minWidth: 65 }} />
                      ) : c.k === "ocupado" ? (row[c.k] ? '✅' : '❌')
                      : ["afiliados","validados","total_usuarios","imp_impresas","imp_entregadas","imp_por_imprimir","convencidos_total","convencidos_afiliados","convencidos_reales","visitas","secciones_ruta","conv_ruta","conv_cot","meta"].includes(c.k)
                        ? num(row[c.k]) : row[c.k]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={cols.length} align="center" sx={{ py: 4, color: "text.secondary" }}>Sin datos</TableCell></TableRow>
            )}
            {filtered.length > 0 && (
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                {cols.map(c => {
                  if (["entidad_nombre","distrito_federal_nombre","plaza","nombre_cot","usuario_nombre","uid","telefono","estatus"].includes(c.k)) return <TableCell key={c.k} sx={{ fontWeight: 700 }}>{c.k === "entidad_nombre" || c.k === "plaza" ? "TOTAL" : ""}</TableCell>;
                  if (c.k === "distrito" || c.k === "seccion" || c.k === "ocupado") return <TableCell key={c.k}></TableCell>;
                  if (c.k === "pct_efectividad") { const a = filtered.reduce((s,r) => s + Number(r.afiliados||0), 0); const v = filtered.reduce((s,r) => s + Number(r.validados||0), 0); return <TableCell key={c.k} align="center" sx={{ fontWeight: 700 }}>{a > 0 ? pct(v/a*100) : "0%"}</TableCell>; }
                  if (c.k === "pct_convencidos") { const t = filtered.reduce((s,r) => s + Number(r.meta||0), 0); const cv = filtered.reduce((s,r) => s + Number(r.convencidos_total||0), 0); return <TableCell key={c.k} align="center" sx={{ fontWeight: 700 }}>{t > 0 ? pct(cv/t*100) : "0%"}</TableCell>; }
                  if (["afiliados","validados","total_usuarios","imp_impresas","imp_entregadas","imp_por_imprimir","convencidos_total","convencidos_afiliados","convencidos_reales","visitas","secciones_ruta","conv_ruta","conv_cot","meta"].includes(c.k)) return <TableCell key={c.k} align="right" sx={{ fontWeight: 700 }}>{num(filtered.reduce((s,r) => s + Number(r[c.k]||0), 0))}</TableCell>;
                  return <TableCell key={c.k}></TableCell>;
                })}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ReportesActividadUsuario;
